import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate, formatDateTime, formatFileSize } from "@/lib/api";
import { logger } from "@/lib/logger";
import { prefillQuoteDetails } from "@/lib/quotePrefill";
import { isPasskeySupported, registerPasskey, listPasskeys, renamePasskey, deletePasskey } from "@/lib/passkeys";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { QuoteDetailsForm } from "@/components/QuoteDetailsForm";
import { CoveragePicker } from "@/components/CoveragePicker";
import { QuoteAttachmentsUploader } from "@/components/QuoteAttachmentsUploader";
import { DropZone } from "@/components/DropZone";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Building2, LogOut, AlertTriangle, Phone, Mail, Car, Plus, Send, FileText, Sparkles, MessageSquare, MessageCircle, CreditCard, Type, KeyRound, Download, Home, ChevronDown, Share2, Fingerprint, Trash2, Pencil } from "lucide-react";
import { PushNotificationsToggle } from "@/components/PushNotificationsToggle";
import { LiveClock } from "@/components/LiveClock";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { usePolicyTypes } from "@/lib/usePolicyTypes";
import { DatePicker } from "@/components/DatePicker";
import { ClientPrivacyConsents } from "@/components/ClientPrivacyConsents";

const TYPE_LABEL = {
  auto: "Auto", casa: "Casa", vita: "Vita", salute: "Salute",
  infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "RC",
};

const QUOTE_TYPES = [
  { v: "auto", l: "Auto" }, { v: "casa", l: "Casa" }, { v: "vita", l: "Vita" },
  { v: "salute", l: "Salute" }, { v: "infortuni", l: "Infortuni" },
  { v: "viaggio", l: "Viaggio" }, { v: "responsabilita_civile", l: "Responsabilità civile" },
];

// 🆕 09/07/2026 — Traduzione IT + raggruppamento in scheda con colori tenui.
const FIELD_LABELS_IT = {
  // Dati proprietario / conducente
  owner_name: "Nome proprietario",
  owner_surname: "Cognome proprietario",
  owner_gender: "Sesso proprietario",
  owner_email: "Email proprietario",
  owner_email_unavailable: "Email proprietario non disponibile",
  owner_birth_date: "Data di nascita proprietario",
  owner_birth_place: "Luogo di nascita proprietario",
  owner_fiscal_code: "Codice Fiscale proprietario",
  owner_address: "Indirizzo proprietario",
  driver_name: "Nome conducente",
  driver_surname: "Cognome conducente",
  driver_gender: "Sesso conducente",
  driver_birth_date: "Data di nascita conducente",
  driver_birth_place: "Luogo di nascita conducente",
  driver_fiscal_code: "Codice Fiscale conducente",
  driver_license_date: "Data patente",
  driver_type: "Tipo di guida",
  driving_type: "Tipo di guida",
  driving_type_manual: "Tipo di guida (inserito manualmente)",
  profession: "Professione",
  marital_status: "Stato civile",
  // Veicolo
  vehicle_plate: "Targa",
  plate: "Targa",
  vehicle_make: "Marca veicolo",
  make: "Marca",
  vehicle_model: "Modello veicolo",
  model: "Modello",
  vehicle_registration_year: "Anno immatricolazione",
  registration_year: "Anno immatricolazione",
  vehicle_kw: "Potenza (kW)",
  kw: "Potenza (kW)",
  vehicle_fuel: "Alimentazione",
  fuel: "Alimentazione",
  vehicle_use: "Uso veicolo",
  vehicle_km_year: "Km/anno",
  km_year: "Km/anno",
  chassis: "Telaio",
  // Polizza
  policy_type: "Tipo polizza",
  policy_expiry: "Scadenza polizza",
  current_company: "Compagnia attuale",
  current_policy_number: "Numero polizza attuale",
  bonus_malus_class: "Classe Bonus/Malus",
  claim_history: "Sinistri ultimi 5 anni",
  selected_coverages: "Garanzie richieste",
  coverages: "Garanzie",
  coverages_auto_text: "Garanzie richieste (testo libero)",
  coverage_request: "Garanzie richieste",
  frazionamento: "Frazionamento",
  // Preferenze
  preferred_contact: "Contatto preferito",
  notes: "Note aggiuntive",
};

const FIELD_GROUPS = {
  personale: {
    title: "Dati personali",
    color: "sky",
    keys: ["owner_name", "owner_surname", "owner_gender", "owner_email", "owner_email_unavailable",
           "owner_birth_date", "owner_birth_place", "owner_fiscal_code", "owner_address",
           "driver_name", "driver_surname", "driver_gender", "driver_birth_date", "driver_birth_place", "driver_fiscal_code", "driver_license_date",
           "profession", "marital_status", "driver_type", "driving_type", "driving_type_manual"],
  },
  veicolo: {
    title: "Veicolo",
    color: "emerald",
    keys: ["vehicle_plate", "plate", "vehicle_make", "make", "vehicle_model", "model",
           "vehicle_registration_year", "registration_year", "vehicle_kw", "kw",
           "vehicle_fuel", "fuel", "vehicle_use", "vehicle_km_year", "km_year", "chassis"],
  },
  polizza: {
    title: "Garanzie richieste",
    color: "violet",
    keys: ["policy_type", "policy_expiry", "current_company", "current_policy_number",
           "bonus_malus_class", "claim_history", "selected_coverages", "coverages",
           "coverages_auto_text", "coverage_request", "frazionamento"],
  },
  preferenze: {
    title: "Preferenze",
    color: "amber",
    keys: ["preferred_contact", "notes"],
  },
};

const GENDER_LABEL = { M: "Maschio", F: "Femmina", male: "Maschio", female: "Femmina", maschio: "Maschio", femmina: "Femmina" };
const FUEL_LABEL = { benzina: "Benzina", diesel: "Diesel", gpl: "GPL", metano: "Metano", elettrica: "Elettrica", ibrida: "Ibrida" };
const DRIVER_TYPE_LABEL = { esclusiva: "Esclusiva", esperta: "Esperta", libera: "Libera" };

function formatFieldValue(key, v) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  // Alcuni valori booleani arrivano come stringa "true"/"false" dal backend
  const sv = String(v).trim();
  if (sv === "true") return "Sì";
  if (sv === "false") return "No";
  if (key.includes("gender")) return GENDER_LABEL[sv] || sv;
  if (key.includes("fuel")) return FUEL_LABEL[sv.toLowerCase()] || sv;
  if (key === "driver_type" || key === "driving_type" || key === "driving_type_manual")
    return DRIVER_TYPE_LABEL[sv.toLowerCase()] || sv;
  if (key.includes("date")) {
    try { const d = new Date(sv); if (!isNaN(d)) return d.toLocaleDateString("it-IT"); } catch { /* ignore */ }
  }
  return sv;
}

function humanize(k) {
  return FIELD_LABELS_IT[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const GROUP_COLORS_MAP = {
  sky:     { bg: "bg-sky-50/60",     border: "border-sky-200",     title: "text-sky-800" },
  emerald: { bg: "bg-emerald-50/60", border: "border-emerald-200", title: "text-emerald-800" },
  violet:  { bg: "bg-violet-50/60",  border: "border-violet-200",  title: "text-violet-800" },
  amber:   { bg: "bg-amber-50/60",   border: "border-amber-200",   title: "text-amber-800" },
  slate:   { bg: "bg-slate-50",      border: "border-slate-200",   title: "text-slate-700" },
};

function QuoteDetailsRows({ details }) {
  const entries = useMemo(
    () => Object.entries(details || {}).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0)),
    [details],
  );
  const grouped = useMemo(() => {
    const map = new Map();
    for (const g of Object.keys(FIELD_GROUPS)) map.set(g, []);
    const usedKeys = new Set();
    for (const [gKey, gCfg] of Object.entries(FIELD_GROUPS)) {
      for (const k of gCfg.keys) {
        const found = entries.find(([ek]) => ek === k);
        if (found) { map.get(gKey).push(found); usedKeys.add(k); }
      }
    }
    const altro = entries.filter(([k]) => !usedKeys.has(k));
    if (altro.length > 0) map.set("altro", altro);
    return map;
  }, [entries]);
  if (entries.length === 0) return <div className="text-[11px] text-slate-400 italic">Nessun dato aggiuntivo</div>;
  return (
    <div className="space-y-2">
      {[...grouped.entries()].map(([gKey, rows]) => {
        if (!rows.length) return null;
        const cfg = FIELD_GROUPS[gKey] || { title: "Altri dati", color: "slate" };
        const c = GROUP_COLORS_MAP[cfg.color] || GROUP_COLORS_MAP.slate;
        return (
          <div key={gKey} className={`rounded-md border ${c.border} ${c.bg} p-2.5`} data-testid={`quote-detail-group-${gKey}`}>
            <div className={`text-[10px] font-bold uppercase tracking-wider ${c.title} mb-1.5`}>{cfg.title}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
              {rows.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-2 text-[11px] leading-relaxed border-b border-white/60 last:border-0 py-0.5">
                  <span className="text-slate-500 shrink-0">{humanize(k)}</span>
                  <span className="font-medium text-slate-800 text-right break-words">{formatFieldValue(k, v)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientArea({ readOnly = false, hideAccountBar = false } = {}) {
  const { user, logout } = useAuth();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const { options: DYN_TYPES, labelByCode: DYN_TYPE_LABEL } = usePolicyTypes();
  const QUOTE_TYPES_LIVE = DYN_TYPES.length ? DYN_TYPES : QUOTE_TYPES;
  const TYPE_LABEL_LIVE = Object.keys(DYN_TYPE_LABEL).length ? DYN_TYPE_LABEL : TYPE_LABEL;
  const [data, setData] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ type: "auto", coverage_request: "", details: {} });
  const [quoteSending, setQuoteSending] = useState(false);
  const [myQuotes, setMyQuotes] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [missingAttachments, setMissingAttachments] = useState([]);
  const [acceptValidity, setAcceptValidity] = useState(false);
  // Aggiornamento scadenze documenti dal cliente
  const [docUpdateOpen, setDocUpdateOpen] = useState(false);
  const [docUpdateTarget, setDocUpdateTarget] = useState(null); // { kind, policy_id, label, current }
  const [docUpdateDate, setDocUpdateDate] = useState("");
  // 🆕 20/07/2026 — Sezioni collassabili nell'Area Cliente (persistiamo in localStorage)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("client_area_collapsed_sections") || "{}"); } catch { return {}; }
  });
  const toggleSection = (key) => {
    setCollapsedSections((s) => {
      const next = { ...s, [key]: !s[key] };
      try { localStorage.setItem("client_area_collapsed_sections", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const [docUpdateNote, setDocUpdateNote] = useState("");
  const [docUpdateSubmitting, setDocUpdateSubmitting] = useState(false);
  // Estrazione AI libretto (drag&drop)
  const [extracting, setExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const extractFromLibretto = async (file) => {
    if (!file || extracting) return;
    setExtracting(true);
    setExtractionError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/my/extract-libretto", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = (data && data.extracted) || {};
      const extracted = Object.fromEntries(
        Object.entries(d).filter(([, v]) => v !== null && v !== undefined && v !== ""),
      );
      const filled = Object.keys(extracted).length;
      if (filled === 0) {
        setExtractionError("L'AI non è riuscita a leggere alcun dato dal libretto. Controlla che l'immagine sia nitida o prova un altro file.");
        return;
      }
      setQuoteForm((q) => ({
        ...q,
        type: "auto",
        details: { ...(q.details || {}), ...extracted },
      }));
      setExtractionDone(true);
      toast.success(`Estratti ${filled} campi dal libretto`);
    } catch (e) {
      setExtractionError(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  };
  // Cambio password
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  // 🆕 Feb 2026 — Passkey / Biometria
  const [passkeyDialogOpen, setPasskeyDialogOpen] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [passkeysLoading, setPasskeysLoading] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeySupported] = useState(() => isPasskeySupported());

  const loadPasskeys = useCallback(async () => {
    setPasskeysLoading(true);
    try {
      const data = await listPasskeys();
      setPasskeys(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPasskeysLoading(false);
    }
  }, []);

  useEffect(() => {
    if (passkeyDialogOpen) loadPasskeys();
  }, [passkeyDialogOpen, loadPasskeys]);

  const onRegisterPasskey = async () => {
    setPasskeyBusy(true);
    try {
      const nomeDispositivo = typeof navigator !== "undefined"
        ? (navigator.userAgentData?.platform || navigator.platform || "Nuovo dispositivo")
        : "Nuovo dispositivo";
      await registerPasskey(nomeDispositivo);
      toast.success("Accesso biometrico attivato su questo dispositivo");
      loadPasskeys();
    } catch (e) {
      toast.error(e?.message || "Impossibile attivare l'accesso biometrico");
    } finally {
      setPasskeyBusy(false);
    }
  };

  const onRenamePasskey = async (pk) => {
    const nome = window.prompt("Nuovo nome per questo dispositivo:", pk.name || "");
    if (!nome || nome.trim() === (pk.name || "").trim()) return;
    try {
      await renamePasskey(pk.id, nome.trim());
      toast.success("Nome aggiornato");
      loadPasskeys();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDeletePasskey = async (pk) => {
    if (!window.confirm(`Rimuovere l'accesso biometrico "${pk.name}"?`)) return;
    try {
      await deletePasskey(pk.id);
      toast.success("Passkey rimossa");
      loadPasskeys();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitChangePassword = async () => {
    if (!pwdForm.current || !pwdForm.next) {
      toast.error("Compila tutti i campi");
      return;
    }
    if (pwdForm.next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdForm.current, new_password: pwdForm.next });
      toast.success("Password aggiornata con successo");
      setPwdOpen(false);
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwdBusy(false);
    }
  };

  // Text zoom (accessibilità): salvato in localStorage. Livelli: 1, 1.15, 1.3
  const FONT_SCALES = [
    { v: 1, label: "A", title: "Testo normale" },
    { v: 1.15, label: "A+", title: "Testo grande" },
    { v: 1.3, label: "A++", title: "Testo extra grande" },
  ];
  const [fontScale, setFontScale] = useState(() => {
    const saved = parseFloat(localStorage.getItem("portal_font_scale") || "1");
    return [1, 1.15, 1.3].includes(saved) ? saved : 1;
  });
  useEffect(() => {
    localStorage.setItem("portal_font_scale", String(fontScale));
  }, [fontScale]);

  const [quotesLoaded, setQuotesLoaded] = useState(false);
  const loadQuotes = async () => {
    try {
      const { data } = await api.get("/my/quotes");
      setMyQuotes(data);
    } catch (e) { logger.error("Failed to load quotes:", e); }
    try {
      const { data } = await api.get("/my/quote-offers");
      setMyOffers(data);
    } catch (e) { logger.error("Failed to load offers:", e); }
    setQuotesLoaded(true);
  };

  const openThread = async (q) => {
    if (expandedQuote === q.id) { setExpandedQuote(null); return; }
    setExpandedQuote(q.id);
    try {
      const { data } = await api.get(`/my/quotes/${q.id}/messages`);
      setThread(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReply = async (qid) => {
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/my/quotes/${qid}/messages`, { body: reply });
      setThread((prev) => [...prev, data]);
      setReply("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    api.get("/my/portfolio")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    loadQuotes();
  }, []);

  // 🆕 Toast di benvenuto: riepilogo priorità (mostrato una volta per sessione)
  const welcomeShownRef = useRef(false);
  useEffect(() => {
    if (welcomeShownRef.current) return;
    // Non mostrare il toast in modalità sola lettura (vista staff sul portale cliente)
    if (readOnly) { welcomeShownRef.current = true; return; }
    if (!data || !data.client) return;
    if (!quotesLoaded) return; // attendi che anche quotes/offers siano caricati
    const alreadyShown = sessionStorage.getItem("clientAreaWelcomeShown") === "1";
    if (alreadyShown) { welcomeShownRef.current = true; return; }

    const inProgressQuotes = myQuotes.filter((q) => q.status === "nuovo" || q.status === "in_lavorazione").length;
    const pendingOffers = myOffers.filter((o) => o.status === "inviata").length;
    // Documenti in scadenza entro 30 giorni
    const daysUntil = (iso) => {
      if (!iso) return null;
      const t = new Date(); t.setHours(0,0,0,0);
      const e = new Date(iso); e.setHours(0,0,0,0);
      return Math.ceil((e - t) / 86400000);
    };
    // Deduplica scadenze veicoli per targa (una sola revisione + un solo bollo per targa)
    const vehiclePlates = new Map();
    (data.policies || []).filter((p) => p.type === "auto" && p.vehicle_plate).forEach((p) => {
      const plate = String(p.vehicle_plate).trim().toUpperCase();
      if (!plate) return;
      const entry = vehiclePlates.get(plate) || { next_review_date: null, bollo_due_date: null };
      if (p.next_review_date && (!entry.next_review_date || new Date(p.next_review_date) > new Date(entry.next_review_date))) entry.next_review_date = p.next_review_date;
      if (p.bollo_due_date && (!entry.bollo_due_date || new Date(p.bollo_due_date) > new Date(entry.bollo_due_date))) entry.bollo_due_date = p.bollo_due_date;
      vehiclePlates.set(plate, entry);
    });
    const docDates = [
      data.client?.driving_license_expires_at,
      data.client?.id_card_expires_at,
      data.client?.health_card_expires_at,
      ...[...vehiclePlates.values()].flatMap((v) => [v.next_review_date, v.bollo_due_date]),
    ];
    const expiringDocs = docDates.filter((d) => {
      const days = daysUntil(d);
      return days !== null && days <= 30;
    }).length;
    // Polizze in scadenza entro 30 giorni
    const expiringPolicies = (data.policies || []).filter((p) => {
      const days = daysUntil(p.end_date);
      return days !== null && days >= 0 && days <= 30;
    }).length;

    const parts = [];
    if (pendingOffers > 0) parts.push(`${pendingOffers} offert${pendingOffers === 1 ? "a" : "e"} da pagare`);
    if (inProgressQuotes > 0) parts.push(`${inProgressQuotes} preventiv${inProgressQuotes === 1 ? "o" : "i"} in lavorazione`);
    if (expiringPolicies > 0) parts.push(`${expiringPolicies} polizz${expiringPolicies === 1 ? "a" : "e"} in scadenza`);
    if (expiringDocs > 0) parts.push(`${expiringDocs} document${expiringDocs === 1 ? "o" : "i"} in scadenza`);

    welcomeShownRef.current = true;
    sessionStorage.setItem("clientAreaWelcomeShown", "1");
    if (parts.length === 0) {
      toast.success(`Ciao ${data.client.name || ""}, tutto in regola! ✨`, { duration: 4000 });
    } else {
      const summary = parts.join(" · ");
      toast.info(`👋 Ciao ${data.client.name || ""} — richiede attenzione: ${summary}`, { duration: 7000 });
    }
  }, [data, myQuotes, myOffers, quotesLoaded]);

  const onLogout = async () => {
    await logout();
    navigate("/portale-clienti");
  };

  const sendQuoteRequest = async () => {
    if (!quoteForm.coverage_request.trim()) return;
    if (!acceptValidity) {
      toast.error("Devi accettare le condizioni di validità del preventivo (5 giorni)");
      return;
    }
    setQuoteSending(true);
    try {
      // strip empty values from details
      const cleanDetails = Object.fromEntries(
        Object.entries(quoteForm.details || {}).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0))
      );
      if (attachmentIds.length > 0) cleanDetails._attachment_ids = attachmentIds;
      cleanDetails._validity_accepted_at = new Date().toISOString();
      await api.post("/my/quote-request", {
        type: quoteForm.type,
        coverage_request: quoteForm.coverage_request,
        details: cleanDetails,
      });
      toast.success("Richiesta inviata. La tua agenzia ti contatterà a breve.");
      setQuoteOpen(false);
      setQuoteForm({ type: "auto", coverage_request: "", details: {} });
      setAttachmentIds([]);
      setAcceptValidity(false);
      loadQuotes();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setQuoteSending(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;

  // 🆕 10/07/2026 — Rimossi tutti gli importi delle polizze dall'area cliente (premio annuo, premio per polizza, ultimo pagamento importo)
  const expiringSoon = data.policies.filter((p) => {
    if (!p.end_date) return false;
    const days = (new Date(p.end_date) - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 30;
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="client-area" style={{ zoom: fontScale }}>
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-md bg-blue-600 flex items-center justify-center overflow-hidden">
              {agency?.logo_url ? (
                <img src={agency.logo_url} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold tracking-tight truncate">{agency?.name || "Assicura"}</div>
              <div className="text-xs text-slate-400">Area riservata cliente</div>
              <div data-testid="role-badge-portal" className="mt-2 inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded border bg-violet-400/15 text-violet-200 border-violet-300/30">
                👤 Cliente
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-start sm:justify-end">
            <PushNotificationsToggle />
            <div data-testid="font-scale-controls" className="inline-flex items-center gap-0.5 bg-white/10 rounded p-0.5" role="group" aria-label="Dimensione testo">
              <Type className="h-3 w-3 text-slate-300 mx-0.5 sm:mx-1" />
              {FONT_SCALES.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  data-testid={`font-scale-${s.v}`}
                  onClick={() => setFontScale(s.v)}
                  className={`text-[10px] sm:text-xs px-1 sm:px-2 py-1 rounded transition-colors ${fontScale === s.v ? "bg-white text-[#0A192F] font-bold" : "text-slate-300 hover:bg-white/10"}`}
                  title={s.title}
                  aria-pressed={fontScale === s.v}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {!hideAccountBar && (
              <>
                <button
                  data-testid="open-change-password"
                  onClick={() => setPwdOpen(true)}
                  className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 inline-flex items-center gap-1 sm:gap-1.5"
                  title="Cambia password"
                  aria-label="Cambia password"
                >
                  <KeyRound className="h-3.5 w-3.5" /> <span className="hidden md:inline">Cambia password</span><span className="hidden sm:inline md:hidden">Pwd</span>
                </button>
                <button
                  data-testid="open-passkeys"
                  onClick={() => setPasskeyDialogOpen(true)}
                  className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 inline-flex items-center gap-1 sm:gap-1.5"
                  title="Accesso biometrico"
                  aria-label="Accesso biometrico"
                >
                  <Fingerprint className="h-3.5 w-3.5" /> <span className="hidden md:inline">Biometria</span><span className="hidden sm:inline md:hidden">Bio</span>
                </button>
                <button
                  data-testid="client-logout"
                  onClick={onLogout}
                  className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 inline-flex items-center gap-1 sm:gap-1.5"
                  aria-label="Esci"
                >
                  <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Esci</span>
                </button>
              </>
            )}
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-3 sm:px-6 pb-2">
          <div className="bg-white/5 rounded px-3 py-1.5 inline-block text-slate-300 text-xs sm:text-sm">
            <LiveClock inline />
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-3 sm:px-6 pb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-2">Benvenuto</p>
          <h1 className="font-display text-xl sm:text-3xl lg:text-4xl font-semibold tracking-tight break-words">
            {data.client.surname} {data.client.name}
          </h1>
          <div className="mt-4 flex gap-2 flex-wrap text-xs text-slate-300">
            {data.client.email && <span className="inline-flex items-center gap-1.5 break-all"><Mail className="h-3 w-3 shrink-0" /> {data.client.email}</span>}
            {data.client.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3 shrink-0" /> {data.client.phone}</span>}
          </div>
        </div>
      </header>

      <main id="client-top" className="max-w-3xl mx-auto px-3 sm:px-6 -mt-6 pb-28 md:pb-12">
        {(() => {
          const inProgressStatuses = new Set(["nuovo", "in_lavorazione"]);
          const inProgressCount = myQuotes.filter((q) => inProgressStatuses.has(q.status)).length;
          const ARCHIVED = new Set(["scaduta", "disdetta", "rinnovata"]);
          const activePoliciesCount = [...(data.policies || []), ...(data.linked_policies || [])].filter((p) => !ARCHIVED.has(p.status)).length;
          return (
            <div className="grid grid-cols-2 gap-1.5 sm:gap-3">
              <div className="bg-white border border-slate-200 rounded-lg p-2 sm:p-3 min-w-0">
                <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-slate-500 leading-tight">Polizze attive</p>
                <p className="text-lg sm:text-2xl font-display font-semibold mt-0.5 tabular-nums" data-testid="client-policies-count">{activePoliciesCount}</p>
              </div>
              <a
                href="#client-quotes-section"
                className={`block bg-white border rounded-lg p-2 sm:p-3 min-w-0 transition-colors ${inProgressCount > 0 ? "border-amber-300 hover:bg-amber-50/40" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid="client-quotes-stat"
              >
                <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-slate-500 leading-tight">Rich. in corso</p>
                <p className={`text-lg sm:text-2xl font-display font-semibold mt-0.5 tabular-nums ${inProgressCount > 0 ? "text-amber-600" : "text-slate-800"}`} data-testid="client-quotes-inprogress-count">
                  {inProgressCount}
                </p>
                <p className="text-[8px] sm:text-[9px] text-slate-400 leading-tight">su {myQuotes.length}</p>
              </a>
            </div>
          );
        })()}

        {expiringSoon.length > 0 && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="client-expiring-alert">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">{expiringSoon.length} polizza{expiringSoon.length > 1 ? "e in scadenza" : " in scadenza"} nei prossimi 30 giorni</p>
              <p className="text-sm text-amber-700 mt-1">Contatta la tua agenzia per il rinnovo.</p>
            </div>
          </div>
        )}

        <section className="mt-8">
          {(() => {
            const pendingOffers = myOffers.filter((o) => o.status === "inviata");
            if (pendingOffers.length === 0) return null;
            return (
              <div className="mb-8 rounded-lg border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 sm:p-5 shadow-md" data-testid="pending-offers-section">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <CreditCard className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-base sm:text-lg text-emerald-900">
                      💰 Offerte da pagare · {pendingOffers.length}
                    </h3>
                    <p className="text-[11px] sm:text-xs text-emerald-700">
                      L&apos;agenzia ti ha inviato {pendingOffers.length === 1 ? "una offerta finale" : "delle offerte finali"}. Per il dettaglio economico contatta il tuo agente.
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mt-3">
                  {pendingOffers.map((o) => {
                    const TYPE_LBL = { auto: "Auto", moto: "Moto", casa: "Casa", vita: "Vita", salute: "Salute", infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "Resp. civile" };
                    const pm = o.payment_methods || {};
                    return (
                      <div key={o.id} data-testid={`offer-${o.id}`} className="bg-white border border-emerald-200 rounded-md p-3 sm:p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-emerald-100 text-emerald-700 border-emerald-200">
                                {TYPE_LBL[o.quote_type] || o.quote_type || "Polizza"}
                              </span>
                              {o.offer_number && (
                                <span className="text-[10px] font-mono text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
                                  {o.offer_number}
                                </span>
                              )}
                              {/* 🆕 10/07/2026 — Importo nascosto: mostrare solo dopo accettazione. Placeholder invita a contattare l'agente. */}
                              <span className="text-[11px] sm:text-xs font-medium text-slate-500 italic">
                                📞 Contatta l&apos;agente per il preventivo
                              </span>
                            </div>
                            <p className="text-[11px] sm:text-xs text-slate-500 mt-1 break-words">
                              {o.coverage_request || "Offerta finale"}
                            </p>
                            {o.valid_until && (
                              <p className="text-[11px] text-amber-700 font-medium mt-1">
                                ⏰ Scade il {formatDate(o.valid_until)} — paga prima di questa data
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Metodi di pagamento accettati (dinamici dalle impostazioni agenzia) */}
                        <div className="flex flex-wrap items-center gap-1.5 mb-3 pt-2 border-t border-slate-100">
                          <span className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold mr-1">Paga con:</span>
                          {pm.stripe_enabled && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700" data-testid="pay-method-stripe">
                              💳 Carta
                            </span>
                          )}
                          {pm.bank_transfer_enabled && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700" data-testid="pay-method-bonifico">
                              🏦 Bonifico bancario
                            </span>
                          )}
                          {pm.paypal_enabled && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700" data-testid="pay-method-paypal">
                              🅿️ PayPal
                            </span>
                          )}
                          {pm.cash_enabled && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700" data-testid="pay-method-cash">
                              💵 Contanti in agenzia
                            </span>
                          )}
                          {!pm.stripe_enabled && !pm.bank_transfer_enabled && !pm.paypal_enabled && !pm.cash_enabled && (
                            <span className="text-[11px] text-slate-400 italic">Contatta l&apos;agenzia per i metodi di pagamento</span>
                          )}
                        </div>
                        {pm.bank_transfer_enabled && pm.iban && (
                          <div className="mb-3 px-2.5 py-2 rounded bg-slate-50 border border-slate-200 text-[11px] sm:text-xs text-slate-700">
                            <div className="font-semibold text-slate-800 mb-0.5">Coordinate bonifico</div>
                            {pm.bank_beneficiary && <div><span className="text-slate-500">Beneficiario:</span> {pm.bank_beneficiary}</div>}
                            {pm.bank_name && <div><span className="text-slate-500">Banca:</span> {pm.bank_name}</div>}
                            <div className="font-mono break-all"><span className="text-slate-500">IBAN:</span> {pm.iban}</div>
                          </div>
                        )}
                        {o.public_url ? (
                          <a
                            href={o.public_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`pay-offer-${o.id}`}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-sm"
                          >
                            <CreditCard className="h-4 w-4" /> Visualizza dettagli e paga
                          </a>
                        ) : (
                          <span className="text-[11px] text-slate-500 italic">Link non disponibile — contatta l&apos;agenzia</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {/* SCADENZE DOCUMENTI · gestite dal cliente */}
          {(() => {
            const dateBadge = (iso) => {
              if (!iso) return { lbl: "non impostata", cls: "bg-slate-100 text-slate-500 border-slate-200" };
              const t = new Date(); t.setHours(0,0,0,0);
              const e = new Date(iso); e.setHours(0,0,0,0);
              const days = Math.ceil((e - t) / 86400000);
              if (days < 0) return { lbl: `scaduta da ${Math.abs(days)} gg`, cls: "bg-red-50 text-red-700 border-red-200", days };
              if (days === 0) return { lbl: "scade oggi", cls: "bg-orange-50 text-orange-700 border-orange-200", days };
              if (days <= 30) return { lbl: `mancano ${days} gg`, cls: "bg-amber-50 text-amber-800 border-amber-200", days };
              return { lbl: `mancano ${days} gg`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", days };
            };
            const rows = [];
            // Patente
            rows.push({
              key: "license",
              kind: "license",
              icon: "🚗",
              label: "Patente di guida",
              meta: data.client?.driving_license_issued_at ? `primo rilascio ${formatDate(data.client.driving_license_issued_at)}` : null,
              current: data.client?.driving_license_expires_at,
              clientUpdated: data.client?.driving_license_expires_client_updated_at,
              policyId: null,
            });
            // 🆔 Carta d'identità
            rows.push({
              key: "id-card",
              kind: "id_card",
              icon: "🆔",
              label: "Carta d'identità",
              meta: null,
              current: data.client?.id_card_expires_at,
              clientUpdated: data.client?.id_card_expires_client_updated_at,
              policyId: null,
            });
            // 💳 Tessera sanitaria
            rows.push({
              key: "health-card",
              kind: "health_card",
              icon: "💳",
              label: "Tessera sanitaria",
              meta: null,
              current: data.client?.health_card_expires_at,
              clientUpdated: data.client?.health_card_expires_client_updated_at,
              policyId: null,
            });
            // 🆕 Feb 2026 — Deduplica per veicolo (targa): mostra una sola riga Bollo e una sola Revisione per targa,
            // selezionando la data più recente valida tra tutte le polizze (attive e archiviate) che condividono lo stesso veicolo.
            const vehicleMap = new Map();
            (data.policies || []).filter((p) => p.type === "auto" && p.vehicle_plate).forEach((p) => {
              const plate = String(p.vehicle_plate).trim().toUpperCase();
              if (!plate) return;
              const entry = vehicleMap.get(plate) || {
                plate: p.vehicle_plate,
                brand: p.vehicle_brand,
                model: p.vehicle_model,
                policyForKey: p,
                last_review_date: null,
                next_review_date: null,
                next_review_date_client_updated_at: null,
                bollo_due_date: null,
                bollo_due_date_client_updated_at: null,
              };
              // Preferisci una polizza attiva per la chiave stabile (id) — se nessuna attiva, usa la più recente per end_date.
              const curActive = ["attiva", "in_scadenza"].includes(entry.policyForKey?.status);
              const newActive = ["attiva", "in_scadenza"].includes(p.status);
              if (newActive && !curActive) entry.policyForKey = p;
              else if (!newActive && !curActive) {
                const curEnd = entry.policyForKey?.end_date ? new Date(entry.policyForKey.end_date) : null;
                const newEnd = p.end_date ? new Date(p.end_date) : null;
                if (newEnd && (!curEnd || newEnd > curEnd)) entry.policyForKey = p;
              }
              // Prendi la data più recente valida per revisione/bollo
              const takeLatest = (curField, curUpdField, newVal, newUpdVal) => {
                if (!newVal) return { v: entry[curField], u: entry[curUpdField] };
                if (!entry[curField]) return { v: newVal, u: newUpdVal || null };
                return new Date(newVal) > new Date(entry[curField])
                  ? { v: newVal, u: newUpdVal || null }
                  : { v: entry[curField], u: entry[curUpdField] };
              };
              const rev = takeLatest("next_review_date", "next_review_date_client_updated_at", p.next_review_date, p.next_review_date_client_updated_at);
              entry.next_review_date = rev.v;
              entry.next_review_date_client_updated_at = rev.u;
              const bol = takeLatest("bollo_due_date", "bollo_due_date_client_updated_at", p.bollo_due_date, p.bollo_due_date_client_updated_at);
              entry.bollo_due_date = bol.v;
              entry.bollo_due_date_client_updated_at = bol.u;
              // last_review_date: prendi la più recente
              if (p.last_review_date && (!entry.last_review_date || new Date(p.last_review_date) > new Date(entry.last_review_date))) {
                entry.last_review_date = p.last_review_date;
              }
              // Marca/modello: prendi il primo non vuoto
              if (!entry.brand && p.vehicle_brand) entry.brand = p.vehicle_brand;
              if (!entry.model && p.vehicle_model) entry.model = p.vehicle_model;
              vehicleMap.set(plate, entry);
            });
            [...vehicleMap.values()].forEach((v) => {
              const vlabel = `${v.plate}${v.brand ? ` · ${v.brand} ${v.model || ""}` : ""}`.trim();
              rows.push({
                key: `rev-${v.plate}`,
                kind: "revisione",
                icon: "🔧",
                label: `Revisione · ${vlabel}`,
                meta: v.last_review_date ? `ultima ${formatDate(v.last_review_date)}` : null,
                current: v.next_review_date,
                clientUpdated: v.next_review_date_client_updated_at,
                policyId: v.policyForKey?.id || null,
              });
              rows.push({
                key: `bollo-${v.plate}`,
                kind: "bollo",
                icon: "🚗",
                label: `Bollo · ${vlabel}`,
                meta: null,
                current: v.bollo_due_date,
                clientUpdated: v.bollo_due_date_client_updated_at,
                policyId: v.policyForKey?.id || null,
              });
            });
            return (
              <section id="client-document-deadlines" className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5 mb-6 scroll-mt-16" data-testid="client-document-deadlines">
                <button
                  type="button"
                  onClick={() => toggleSection("deadlines")}
                  className="w-full flex items-center justify-between gap-3 mb-3 group cursor-pointer select-none rounded-md hover:bg-slate-50 -mx-2 px-2 py-1 transition-colors"
                  aria-expanded={!collapsedSections.deadlines}
                  data-testid="toggle-section-deadlines"
                >
                  <h2 className="font-display text-base sm:text-xl font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5 text-violet-600" /> Scadenze documenti
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold tabular-nums">{rows.length}</span>
                  </h2>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 group-hover:bg-slate-200 text-slate-700 text-[11px] font-semibold shrink-0 transition-colors">
                    {collapsedSections.deadlines ? "Mostra" : "Nascondi"}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsedSections.deadlines ? "" : "rotate-180"}`} />
                  </span>
                </button>
                {!collapsedSections.deadlines && (
                  <>
                    <p className="text-[11px] text-slate-500 mb-3">Se hai rinnovato un documento, comunicalo qui — l&apos;agenzia riceverà la nuova data.</p>
                    <div className="space-y-2">
                      {rows.map((r) => {
                        const b = dateBadge(r.current);
                        return (
                          <div key={r.key} className="flex items-start sm:items-center justify-between gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 rounded-md border border-slate-200 bg-slate-50/50 flex-wrap" data-testid={`doc-row-${r.kind}-${r.policyId || "client"}`}>
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <span className="text-xl sm:text-2xl leading-none">{r.icon}</span>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-sm sm:text-base text-slate-900 break-words">{r.label}</p>
                                <p className="text-xs sm:text-sm text-slate-800 break-words">{r.current ? <>scade il <strong>{formatDate(r.current)}</strong></> : "data non comunicata"}{r.meta ? ` · ${r.meta}` : ""}</p>
                                {r.clientUpdated && (
                                  <p className="text-[11px] sm:text-xs text-violet-700 mt-0.5 font-semibold inline-flex items-center gap-1">
                                    ✓ aggiornata da te il {formatDate(r.clientUpdated)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end w-full sm:w-auto">
                              <span className={`text-[11px] sm:text-xs uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${b.cls}`}>{b.lbl}</span>
                              {!readOnly && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`doc-update-${r.kind}-${r.policyId || "client"}`}
                                onClick={() => {
                                  setDocUpdateTarget({ kind: r.kind, policy_id: r.policyId, label: r.label, current: r.current });
                                  setDocUpdateDate(r.current || "");
                                  setDocUpdateNote("");
                                  setDocUpdateOpen(true);
                                }}
                                className="text-[11px] sm:text-xs h-7 px-2 sm:px-2.5"
                              >
                                ✏️ Aggiorna
                              </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>
            );
          })()}

          <div id="client-policies-section" className="flex items-center justify-between mb-4 gap-2 flex-wrap scroll-mt-16">
            <button
              type="button"
              onClick={() => toggleSection("policies")}
              className="flex items-center gap-2 group cursor-pointer select-none rounded-md hover:bg-slate-50 -mx-2 px-2 py-1 transition-colors"
              aria-expanded={!collapsedSections.policies}
              data-testid="toggle-section-policies"
            >
              <h2 className="font-display text-lg sm:text-xl font-semibold">Le mie polizze</h2>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 group-hover:bg-slate-200 text-slate-700 text-[11px] font-semibold shrink-0 transition-colors">
                {collapsedSections.policies ? "Mostra" : "Nascondi"}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsedSections.policies ? "" : "rotate-180"}`} />
              </span>
            </button>
            <Dialog open={quoteOpen} onOpenChange={(o) => {
              setQuoteOpen(o);
              if (o) {
                setQuoteForm({ type: "auto", coverage_request: "", details: prefillQuoteDetails(data?.client, "auto") });
              }
            }}>
              {!readOnly && (
              <DialogTrigger asChild>
                <Button data-testid="request-quote-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="h-4 w-4 mr-2" /> Richiedi preventivo
                </Button>
              </DialogTrigger>
              )}
              <DialogContent className="w-[95vw] max-w-6xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-blue-600" /> Richiedi un preventivo
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">Compila i dati specifici e la tua agenzia ti contatterà con un&apos;offerta personalizzata.</p>
                  <div>
                    <Label>Tipo di polizza</Label>
                    <Select value={quoteForm.type} onValueChange={(v) => {
                      setQuoteForm({ ...quoteForm, type: v, details: prefillQuoteDetails(data?.client, v) });
                      setExtractionDone(false);
                      setExtractionError(null);
                    }}>
                      <SelectTrigger data-testid="quote-req-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{QUOTE_TYPES_LIVE.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {quoteForm.type === "auto" && (
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-1.5">⚡ Compilazione automatica da libretto</p>
                      <p className="text-xs text-slate-500 mb-2.5">
                        Carica una foto/PDF del libretto di circolazione: i dati del veicolo verranno estratti automaticamente e compilati nei campi qui sotto.
                      </p>
                      <label className={`block cursor-pointer ${extracting ? "pointer-events-none" : ""}`}>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          disabled={extracting}
                          data-testid="client-libretto-input"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) extractFromLibretto(f);
                            e.target.value = "";
                          }}
                        />
                        <DropZone
                          testid="client-libretto-drop"
                          accept={["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]}
                          multiple={false}
                          disabled={extracting}
                          onFiles={(files) => { if (files?.[0]) extractFromLibretto(files[0]); }}
                          className={`rounded-md border-2 border-dashed ${extractionDone ? "border-emerald-300 bg-emerald-50/40" : "border-blue-300 bg-blue-50/30"}`}
                        >
                          <div className={`flex flex-col items-center justify-center py-6 px-4 text-center ${extracting ? "opacity-60" : ""}`}>
                            {extracting ? (
                              <>
                                <div className="animate-spin h-7 w-7 rounded-full border-2 border-blue-300 border-t-blue-600 mb-2"></div>
                                <p className="text-sm font-medium text-blue-700">Analisi in corso…</p>
                                <p className="text-xs text-slate-500 mt-1">L&apos;AI sta leggendo il documento</p>
                              </>
                            ) : extractionDone ? (
                              <>
                                <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
                                  <span className="text-emerald-700 text-lg">✓</span>
                                </div>
                                <p className="text-sm font-medium text-emerald-700">Dati estratti correttamente</p>
                                <p className="text-xs text-slate-500 mt-1">Trascina/seleziona un altro file per sostituire</p>
                              </>
                            ) : (
                              <>
                                <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                                  <span className="text-blue-700 text-lg">📕</span>
                                </div>
                                <p className="text-sm font-medium text-slate-700">Trascina qui il libretto</p>
                                <p className="text-xs text-slate-500 mt-1">JPG · PNG · PDF · HEIC (foto da iPhone) · max 10 MB</p>
                                <p className="text-xs text-blue-600 mt-1.5 underline">o clicca per selezionare</p>
                              </>
                            )}
                          </div>
                        </DropZone>
                      </label>
                      {/* 🆕 10/07/2026 — Scanner / Camera per libretto (allineato a Polizze) */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-[11px] text-slate-500">oppure</span>
                        <ScanButton
                          testid="scan-client-libretto"
                          label="Importa da scanner"
                          variant="outline"
                          disabled={extracting}
                          onFile={(f) => extractFromLibretto(f)}
                        />
                        <CameraButton
                          testid="cam-client-libretto"
                          label="📷 Foto da fotocamera"
                          variant="outline"
                          disabled={extracting}
                          onFile={(f) => extractFromLibretto(f)}
                        />
                      </div>
                      {extractionError && (
                        <div data-testid="client-libretto-error" className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                          <span className="font-semibold">Errore:</span> {extractionError}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-3">Dati specifici per {QUOTE_TYPES_LIVE.find((t) => t.v === quoteForm.type)?.l}</p>
                    <QuoteDetailsForm
                      type={quoteForm.type}
                      details={quoteForm.details}
                      onChange={(newDetails) => setQuoteForm({ ...quoteForm, details: newDetails })}
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <Label>Garanzie di interesse <span className="text-[10px] font-normal text-slate-500">(spunta le coperture che ti interessano)</span></Label>
                    <CoveragePicker
                      publicMode
                      testid="client-coverages"
                      policyType={quoteForm.type}
                      value={quoteForm.details?.selected_coverages || []}
                      onChange={(codes, labels) => {
                        const composedText = labels.join(" · ");
                        setQuoteForm((q) => {
                          const prevAuto = q.details?._coverages_auto_text || "";
                          const currentText = (q.coverage_request || "").trim();
                          const nextText = (!currentText || currentText === prevAuto) ? composedText : currentText;
                          return {
                            ...q,
                            details: { ...(q.details || {}), selected_coverages: codes, _coverages_auto_text: composedText },
                            coverage_request: nextText,
                          };
                        });
                      }}
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <Label>Note aggiuntive / Descrivi cosa ti serve <span className="text-red-500">*</span></Label>
                    <Textarea
                      data-testid="quote-req-coverage"
                      rows={3}
                      placeholder="Aggiungi qualsiasi informazione utile (richieste particolari, preferenze sulle garanzie, ecc.)"
                      value={quoteForm.coverage_request}
                      onChange={(e) => setQuoteForm({ ...quoteForm, coverage_request: e.target.value })}
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <QuoteAttachmentsUploader
                      onChange={setAttachmentIds}
                      onStatusChange={({ missing }) => setMissingAttachments(missing || [])}
                      isAuto={quoteForm.type === "auto"}
                      showBersani={quoteForm.type === "auto" && quoteForm.details?.bersani_applicable === true}
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4 space-y-2">
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="text-xs font-semibold text-amber-900 mb-1">⚠️ Validità del preventivo</p>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        L&apos;importo del premio assicurativo comunicato a seguito della tua richiesta ha <strong>validità di 5 giorni dalla data di emissione</strong>. Trascorso tale termine, il premio potrebbe subire variazioni in base agli aggiornamenti tariffari della compagnia, alle modifiche normative o ai parametri di rischio aggiornati. Conferma la prenotazione del preventivo entro 5 giorni per garantire l&apos;importo proposto.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="accept-validity-wrap">
                      <Checkbox
                        data-testid="accept-validity"
                        checked={acceptValidity}
                        onCheckedChange={(v) => setAcceptValidity(v === true)}
                      />
                      <span>Ho letto e accetto le condizioni di validità del preventivo (5 giorni) <span className="text-red-500">*</span></span>
                    </label>
                  </div>
                </div>
                <DialogFooter>
                  {(() => {
                    const missing = [];
                    if (!quoteForm.coverage_request.trim()) missing.push("Note aggiuntive / Descrivi cosa ti serve");
                    missingAttachments.forEach((m) => missing.push(`Allegato: ${m}`));
                    if (!acceptValidity) missing.push("Conferma le condizioni di validità del preventivo (5 giorni)");
                    if (missing.length === 0) return null;
                    return (
                      <div data-testid="quote-missing-fields" className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 mr-auto">
                        <div className="font-semibold mb-1 flex items-center gap-1.5">
                          ⚠️ Per inviare la richiesta manca:
                        </div>
                        <ul className="list-disc list-inside space-y-0.5">
                          {missing.map((m) => <li key={m}>{m}</li>)}
                        </ul>
                      </div>
                    );
                  })()}
                  <Button variant="outline" onClick={() => setQuoteOpen(false)}>Annulla</Button>
                  <Button
                    data-testid="send-quote-btn"
                    onClick={sendQuoteRequest}
                    disabled={quoteSending || !quoteForm.coverage_request.trim() || !acceptValidity || missingAttachments.length > 0}
                    className="bg-[#0F172A] hover:bg-[#1E293B]"
                  >
                    {quoteSending ? "Invio…" : <><Send className="h-4 w-4 mr-2" /> Invia richiesta</>}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {!collapsedSections.policies && (
          <>
          <div className="space-y-3" data-testid="client-policies-list">
            {(() => {
              // 🆕 Feb 2026 — Separa polizze attive da polizze archiviate (scadute/disdette/rinnovate).
              // Le archiviate vengono mostrate in una sezione dedicata "Archivio Polizze" (max 3 per veicolo).
              const own = data.policies || [];
              const linked = data.linked_policies || [];
              const all = [...own, ...linked];
              const ARCHIVED = new Set(["scaduta", "disdetta", "rinnovata"]);
              const activeList = all.filter((p) => !ARCHIVED.has(p.status));
              return activeList.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
                  Nessuna polizza attiva. Contatta la tua agenzia.
                </div>
              ) : null;
            })()}
            {[...(data.policies || []), ...(data.linked_policies || [])]
              .filter((p) => !["scaduta", "disdetta", "rinnovata"].includes(p.status))
              .map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-3 sm:p-5" data-testid={`client-policy-${p.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ShieldCheck className="h-4 w-4 text-blue-600 shrink-0" />
                      <span className="font-display font-semibold text-base sm:text-lg">{TYPE_LABEL_LIVE[p.type] || p.type}</span>
                      {p._linked_owner && (
                        <span
                          data-testid={`policy-linked-owner-${p.id}`}
                          className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border bg-violet-50 border-violet-200 text-violet-700"
                          title="Polizza collegata alla tua area privata"
                        >
                          👤 Intestata a: {p._linked_owner.display}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 break-words">{p.company} · n° {p.policy_number}</p>
                    {p.vehicle_plate && (
                      <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                        <Car className="h-3 w-3" />
                        <span className="font-mono">{p.vehicle_plate}</span>
                        {p.vehicle_brand && <span>· {p.vehicle_brand} {p.vehicle_model}</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusBadge status={p.status} />
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Scadenza annuale</span>
                    <span className="text-xs font-semibold tabular-nums text-slate-700">{formatDate(p.end_date)}</span>
                    {p.effective_end_date && p.effective_end_date !== p.end_date && (
                      <>
                        <span className="text-[10px] uppercase tracking-wide text-blue-400 leading-none mt-1">Prossima rata</span>
                        <span className="text-xs font-semibold tabular-nums text-blue-700">{formatDate(p.effective_end_date)}</span>
                      </>
                    )}
                    {(() => {
                      // 🆕 10/07/2026 — Countdown scadenza polizza: verde/ambra/rosso in base ai giorni residui
                      if (!p.end_date) return null;
                      const days = Math.ceil((new Date(p.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                      if (days < 0) {
                        return (
                          <span data-testid={`policy-countdown-${p.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-rose-100 text-rose-700 border-rose-300">
                            Scaduta da {Math.abs(days)} gg
                          </span>
                        );
                      }
                      if (days === 0) {
                        return (
                          <span data-testid={`policy-countdown-${p.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-rose-100 text-rose-700 border-rose-300 animate-pulse">
                            Scade oggi
                          </span>
                        );
                      }
                      if (days > 60) return null;
                      const cls = days < 7
                        ? "bg-rose-100 text-rose-700 border-rose-300 animate-pulse"
                        : days < 30
                          ? "bg-amber-100 text-amber-800 border-amber-300"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200";
                      return (
                        <span data-testid={`policy-countdown-${p.id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
                          Mancano {days} gg
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {p.status !== "disdetta" && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                    {(() => {
                      // 🆕 10/07/2026 — Effective paid_at + stato pagamenti rate
                      // 🆕 Feb 2026 — Logica riscritta su ISO string (safe timezone) e con
                      // "scadenza rata successiva" per la prima rata futura non pagata.
                      let effectivePaidAt = p.last_payment_at;
                      const paidDates = Object.values(p.installment_paid_dates || {}).filter(Boolean);
                      if (paidDates.length > 0) {
                        const all = [...paidDates, ...(p.last_payment_at ? [p.last_payment_at] : [])];
                        effectivePaidAt = all.sort().slice(-1)[0];
                      }
                      const paymentStatus = (() => {
                        const pd = p.installment_paid_dates || {};
                        // Preferisci le scadenze calcolate dal backend (`installment_dates`)
                        let dates = Array.isArray(p.installment_dates)
                          ? p.installment_dates.filter(Boolean).map((d) => String(d).slice(0, 10))
                          : [];
                        // Fallback: ricostruisci da start_date/end_date/frazionamento (ISO-safe)
                        if (dates.length === 0 && p.start_date && p.end_date) {
                          const FRAZ_MONTHS = { annuale: 12, semestrale: 6, quadrimestrale: 4, trimestrale: 3, mensile: 1 };
                          const months = FRAZ_MONTHS[p.frazionamento] || 12;
                          const startIso = String(p.start_date).slice(0, 10);
                          const endIso = String(p.end_date).slice(0, 10);
                          if (endIso > startIso) {
                            const parts = startIso.split("-").map(Number);
                            let [y, m, d] = parts;
                            for (let i = 0; i < 60; i++) {
                              const iso = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                              if (iso >= endIso) break;
                              dates.push(iso);
                              // Aggiungi `months` mesi senza timezone drift
                              const total = (y * 12 + (m - 1)) + months;
                              y = Math.floor(total / 12);
                              m = (total % 12) + 1;
                            }
                          }
                        }
                        if (dates.length === 0) return { hasInstallments: false, arrears: 0, allPaid: false, nextDueDate: null };
                        // ISO string di oggi in fuso locale (evita drift UTC)
                        const now = new Date();
                        const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
                        let dueCount = 0, paidCount = 0, nextDueDate = null;
                        for (const dIso of dates) {
                          const isPaid = !!pd[dIso];
                          if (dIso <= todayIso) {
                            // Rata scaduta (data odierna o passata)
                            dueCount += 1;
                            if (isPaid) paidCount += 1;
                          } else if (!isPaid && !nextDueDate) {
                            // Prima rata futura non pagata
                            nextDueDate = dIso;
                          }
                        }
                        return {
                          hasInstallments: dates.length > 0,
                          arrears: Math.max(0, dueCount - paidCount),
                          allPaid: dueCount > 0 && paidCount === dueCount,
                          nextDueDate,
                        };
                      })();
                      const nextDueLabel = paymentStatus.nextDueDate ? (
                        <span
                          data-testid={`policy-next-due-${p.id}`}
                          className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sky-100 border border-sky-300 text-sky-800"
                          title="Data di scadenza della prossima rata"
                        >
                          Scadenza rata successiva: {formatDate(paymentStatus.nextDueDate)}
                        </span>
                      ) : null;
                      if (effectivePaidAt) {
                        return (
                          <span className="text-[11px] sm:text-xs text-emerald-700 inline-flex items-center gap-1.5 min-w-0 break-words flex-wrap">
                            <CreditCard className="h-3.5 w-3.5 shrink-0" />
                            <span className="break-words">Ultimo pagamento: {formatDate(effectivePaidAt)}</span>
                            {paymentStatus.arrears === 0 && paymentStatus.allPaid && !paymentStatus.nextDueDate && (
                              <span
                                data-testid={`policy-in-regola-${p.id}`}
                                className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800"
                                title="Tutte le rate scadute risultano pagate"
                              >
                                ✓ In regola
                              </span>
                            )}
                            {paymentStatus.arrears === 0 && nextDueLabel}
                            {paymentStatus.arrears > 0 && (
                              <span
                                data-testid={`policy-arretrate-${p.id}`}
                                className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-100 border border-rose-300 text-rose-700 animate-pulse"
                                title="Ci sono rate scadute non ancora pagate"
                              >
                                ⚠️ {paymentStatus.arrears} rat{paymentStatus.arrears === 1 ? "a" : "e"} arretrat{paymentStatus.arrears === 1 ? "a" : "e"}
                              </span>
                            )}
                          </span>
                        );
                      }
                      // Nessun pagamento MAI + rate scadute → arretrate
                      if (paymentStatus.arrears > 0) {
                        return (
                          <span
                            data-testid={`policy-arretrate-${p.id}`}
                            className="text-[11px] sm:text-xs text-rose-700 inline-flex items-center gap-1.5 font-semibold"
                          >
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            {paymentStatus.arrears} rat{paymentStatus.arrears === 1 ? "a" : "e"} arretrat{paymentStatus.arrears === 1 ? "a" : "e"}: contatta il tuo agente
                          </span>
                        );
                      }
                      // Nessun pagamento ma prima rata è futura → mostra la data successiva
                      if (paymentStatus.nextDueDate) {
                        return (
                          <span
                            data-testid={`policy-next-due-${p.id}`}
                            className="text-[11px] sm:text-xs text-sky-700 inline-flex items-center gap-1.5"
                          >
                            <CreditCard className="h-3.5 w-3.5 shrink-0" />
                            Scadenza rata successiva: {formatDate(paymentStatus.nextDueDate)}
                          </span>
                        );
                      }
                      return (
                        <span className="text-[11px] sm:text-xs text-slate-500">Nessun pagamento registrato</span>
                      );
                    })()}
                    {/* 🆕 10/07/2026 — Bottone WhatsApp "Richiedi rinnovo" se in scadenza (≤60 gg) */}
                    {(() => {
                      if (!p.end_date || !agency?.phone) return null;
                      const days = Math.ceil((new Date(p.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                      if (days > 60) return null;
                      const cleanPhone = String(agency.phone).replace(/[^0-9+]/g, "").replace(/^\+/, "");
                      if (!cleanPhone) return null;
                      const msg = `Ciao, vorrei rinnovare la polizza ${p.policy_number || ""} in scadenza il ${formatDate(p.end_date)}.`;
                      const waUrl = `whatsapp://send?phone=${cleanPhone}&text=${encodeURIComponent(msg)}`;
                      return (
                        <a
                          href={waUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`policy-renew-wa-${p.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold px-2.5 py-1 rounded-md bg-[#25D366] text-white hover:bg-[#128C7E] shadow-sm transition-colors"
                          title="Chatta con il tuo agente per il rinnovo"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          Richiedi rinnovo
                        </a>
                      );
                    })()}
                  </div>
                )}
                {/* 🆕 Allegati polizza condivisi con il cliente (solo se polizza ATTIVA) */}
                {p.status === "attiva" && (() => {
                  const attsForPolicy = (data.visible_attachments || []).filter((a) => a.policy_id === p.id);
                  if (attsForPolicy.length === 0) return null;
                  return (
                    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`policy-shared-files-${p.id}`}>
                      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                        <FileText className="h-3.5 w-3.5 text-blue-600" />
                        Documenti polizza condivisi
                        <span className="text-[10px] font-normal text-slate-400">({attsForPolicy.length})</span>
                      </p>
                      <div className="space-y-1.5">
                        {attsForPolicy.map((att) => {
                          const doDownload = async () => {
                            // 🆕 20/07/2026 — Signed URL download: bypassa blob-URL su mobile.
                            // Il server firma un token breve (120s), il browser naviga direttamente
                            // e rispetta Content-Disposition scaricando col nome originale senza
                            // il prompt "SAVE AS" con nome vuoto su Android Chrome / iOS Safari.
                            try {
                              const { data } = await api.post(`/my/policy-attachment/${att.id}/download-url`, { mode: "download" });
                              const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                              const fullUrl = `${backendBase}${data.url}`;
                              // Naviga direttamente: browser scarica col Content-Disposition dal server
                              window.location.href = fullUrl;
                            } catch (e) {
                              const status = e?.response?.status;
                              const detail = e?.response?.data?.detail;
                              toast.error(`Errore download${status ? " (" + status + ")" : ""}: ${detail || e.message || "riprova più tardi"}`);
                            }
                          };
                          const doShare = async () => {
                            // 🆕 20/07/2026 — Condivisione via Web Share API (WhatsApp, Email, AirDrop, ecc.).
                            // Scarica il file via signed URL e lo condivide come File nativo del dispositivo.
                            const filename = att.original_filename || att.filename || `documento-${att.id}`;
                            try {
                              const { data: signed } = await api.post(`/my/policy-attachment/${att.id}/download-url`, { mode: "download" });
                              const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                              const resp = await fetch(`${backendBase}${signed.url}`);
                              if (!resp.ok) throw new Error("download failed");
                              const blob = await resp.blob();
                              const file = new File([blob], filename, { type: att.content_type || blob.type || "application/octet-stream" });
                              if (navigator.canShare && navigator.canShare({ files: [file] })) {
                                await navigator.share({
                                  files: [file],
                                  title: filename,
                                  text: `${filename} - Polizza`,
                                });
                              } else if (navigator.share) {
                                // Alcuni browser supportano share ma non files: mandiamo solo il testo
                                await navigator.share({ title: filename, text: `${filename} - Polizza` });
                                toast.info("Il tuo dispositivo non supporta la condivisione file. Usa 'Scarica' e allega manualmente.");
                              } else {
                                // Fallback desktop: apri WhatsApp Web con solo il testo
                                const msg = encodeURIComponent(`Ti condivido: ${filename}`);
                                window.open(`whatsapp://send?text=${msg}`, "_blank", "noopener,noreferrer");
                                toast.info("Su desktop condividi solo il testo — allega manualmente il file scaricato.");
                              }
                            } catch (e) {
                              if (e?.name === "AbortError") return; // utente ha annullato
                              toast.error(`Errore condivisione: ${e?.message || "riprova"}`);
                            }
                          };
                          return (
                            <div
                              key={att.id}
                              data-testid={`policy-attachment-row-${att.id}`}
                              className="flex items-center gap-2 px-2 sm:px-2.5 py-1.5 rounded border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 text-xs transition-colors group"
                            >
                              <FileText className="h-3.5 w-3.5 text-slate-400 group-hover:text-blue-600 shrink-0" />
                              <span className="flex-1 truncate font-medium text-slate-700 min-w-0">{att.original_filename}</span>
                              <span className="hidden sm:inline text-[10px] text-slate-400 shrink-0">{formatFileSize(att.size || 0)}</span>
                              <button
                                type="button"
                                onClick={doShare}
                                data-testid={`share-shared-file-${att.id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 text-[11px] font-semibold shadow-sm transition-colors shrink-0"
                                title={`Condividi ${att.original_filename} via WhatsApp/Email`}
                                aria-label={`Condividi ${att.original_filename}`}
                              >
                                <Share2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Condividi</span>
                              </button>
                              <button
                                type="button"
                                onClick={doDownload}
                                data-testid={`download-shared-file-${att.id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-blue-300 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold shadow-sm transition-colors shrink-0"
                                title={`Scarica ${att.original_filename}`}
                                aria-label={`Scarica ${att.original_filename}`}
                              >
                                <Download className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Scarica</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
          {/* 🆕 Feb 2026 — Archivio Polizze: mostra al massimo 3 polizze scadute/disdette per veicolo (collassabile, chiuso di default) */}
          {(() => {
            const all = [...(data.policies || []), ...(data.linked_policies || [])];
            const archived = all.filter((p) => ["scaduta", "disdetta", "rinnovata"].includes(p.status));
            if (archived.length === 0) return null;
            // Group by plate; policies without plate go in a "no-plate" bucket
            const byPlate = new Map();
            const noPlate = [];
            for (const p of archived) {
              if (p.vehicle_plate) {
                const k = String(p.vehicle_plate).trim().toUpperCase();
                if (!byPlate.has(k)) byPlate.set(k, []);
                byPlate.get(k).push(p);
              } else {
                noPlate.push(p);
              }
            }
            const sortByEndDesc = (a, b) => new Date(b.end_date || 0) - new Date(a.end_date || 0);
            for (const [k, arr] of byPlate) byPlate.set(k, arr.sort(sortByEndDesc).slice(0, 3));
            noPlate.sort(sortByEndDesc);
            const totalShown = [...byPlate.values()].reduce((n, arr) => n + arr.length, 0) + noPlate.length;
            return (
              <details className="mt-6 rounded-lg border border-slate-200 bg-slate-50/50 group" data-testid="client-archive-policies">
                <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-2 text-slate-700 hover:bg-slate-100/60 rounded-lg">
                  <span className="inline-flex items-center gap-2 font-semibold text-sm">
                    <FileText className="h-4 w-4 text-slate-500" />
                    Archivio Polizze
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 tabular-nums">{totalShown}</span>
                  </span>
                  <span className="text-[11px] text-slate-500 group-open:hidden">Mostra</span>
                  <span className="text-[11px] text-slate-500 hidden group-open:inline">Nascondi</span>
                </summary>
                <div className="p-3 sm:p-4 border-t border-slate-200 space-y-4">
                  <p className="text-[11px] text-slate-500 italic">
                    Ultime {byPlate.size > 0 ? "3 polizze per veicolo" : "polizze"} non più attive (scadute, disdette o rinnovate).
                  </p>
                  {[...byPlate.entries()].map(([plate, arr]) => (
                    <div key={plate} data-testid={`archive-group-${plate}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <Car className="h-3 w-3" />
                        <span className="font-mono">{plate}</span>
                        {arr[0]?.vehicle_brand && <span className="text-slate-400 normal-case tracking-normal">· {arr[0].vehicle_brand} {arr[0].vehicle_model || ""}</span>}
                      </div>
                      <div className="space-y-2">
                        {arr.map((p) => (
                          <div key={p.id} className="bg-white border border-slate-200 rounded-md px-3 py-2 flex items-start justify-between gap-2 flex-wrap" data-testid={`archive-policy-${p.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-xs text-slate-700">{TYPE_LABEL_LIVE[p.type] || p.type}</span>
                                <span className="text-[10px] font-mono text-slate-400">n° {p.policy_number}</span>
                                {p._linked_owner && (
                                  <span
                                    className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border bg-violet-50 border-violet-200 text-violet-700"
                                    data-testid={`archive-linked-${p.id}`}
                                  >
                                    👤 {p._linked_owner.display}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {p.company}
                                {p.start_date && p.end_date && (
                                  <> · {formatDate(p.start_date)} → {formatDate(p.end_date)}</>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0">
                              <StatusBadge status={p.status} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {noPlate.length > 0 && (
                    <div data-testid="archive-group-no-plate">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Altre polizze</div>
                      <div className="space-y-2">
                        {noPlate.map((p) => (
                          <div key={p.id} className="bg-white border border-slate-200 rounded-md px-3 py-2 flex items-start justify-between gap-2 flex-wrap" data-testid={`archive-policy-${p.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-xs text-slate-700">{TYPE_LABEL_LIVE[p.type] || p.type}</span>
                                <span className="text-[10px] font-mono text-slate-400">n° {p.policy_number}</span>
                                {p._linked_owner && (
                                  <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border bg-violet-50 border-violet-200 text-violet-700">
                                    👤 {p._linked_owner.display}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {p.company}
                                {p.start_date && p.end_date && (
                                  <> · {formatDate(p.start_date)} → {formatDate(p.end_date)}</>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0">
                              <StatusBadge status={p.status} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </details>
            );
          })()}
          </>
          )}
        </section>

        <section className="mt-10 scroll-mt-16" id="client-quotes-section" data-testid="client-quotes-section">
          <button
            type="button"
            onClick={() => toggleSection("quotes")}
            className="w-full flex items-center justify-between gap-2 mb-4 group cursor-pointer select-none rounded-md hover:bg-slate-50 -mx-2 px-2 py-1 transition-colors"
            aria-expanded={!collapsedSections.quotes}
            data-testid="toggle-section-quotes"
          >
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Le mie richieste preventivo
              {myQuotes.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold tabular-nums">{myQuotes.length}</span>}
            </h2>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 group-hover:bg-slate-200 text-slate-700 text-[11px] font-semibold shrink-0 transition-colors">
              {collapsedSections.quotes ? "Mostra" : "Nascondi"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsedSections.quotes ? "" : "rotate-180"}`} />
            </span>
          </button>
          {!collapsedSections.quotes && (
          <>
          {myQuotes.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-400">
              Non hai ancora inviato richieste. Usa il pulsante &quot;Richiedi preventivo&quot; qui sopra.
            </div>
          ) : (
            <div className="space-y-2" data-testid="client-quotes-list">
              {myQuotes.map((q) => {
                // Colore card in base allo stato del preventivo
                const STATUS_THEME = {
                  nuovo:          { bar: "bg-blue-500",   bg: "bg-blue-50/30",    border: "border-blue-200" },
                  in_lavorazione: { bar: "bg-amber-500",  bg: "bg-amber-50/40",   border: "border-amber-200" },
                  accettato:     { bar: "bg-emerald-500",bg: "bg-emerald-50/40", border: "border-emerald-200" },
                  rifiutato:     { bar: "bg-rose-500",   bg: "bg-rose-50/30",    border: "border-rose-200" },
                };
                const theme = STATUS_THEME[q.status] || STATUS_THEME.nuovo;
                const TYPE_COLORS = {
                  auto: "bg-emerald-100 text-emerald-700 border-emerald-200",
                  moto: "bg-purple-100 text-purple-700 border-purple-200",
                  casa: "bg-orange-100 text-orange-700 border-orange-200",
                  vita: "bg-pink-100 text-pink-700 border-pink-200",
                  salute: "bg-rose-100 text-rose-700 border-rose-200",
                  infortuni: "bg-amber-100 text-amber-700 border-amber-200",
                  viaggio: "bg-sky-100 text-sky-700 border-sky-200",
                  responsabilita_civile: "bg-slate-100 text-slate-700 border-slate-200",
                };
                const typeColor = TYPE_COLORS[q.type] || "bg-blue-100 text-blue-700 border-blue-200";
                // 🆕 Feb 2026 — Se l'agente ha già inviato al cliente un'offerta finale collegata a questa
                //   richiesta, mostriamo il badge "📧 Preventivo elaborato e inviato via email" al posto
                //   dello stato "In lavorazione".
                const linkedOffer = (myOffers || []).find((o) => o && o.quote_id === q.id && (o.status === "inviata" || o.status === "accettata"));
                const _VEHICLE_TYPES = new Set(["auto", "moto", "furgone", "autocarro", "camion"]);
                const _d = q.details || {};
                const _brand = (_d.vehicle_brand || "").toString().trim();
                const _model = (_d.vehicle_model || "").toString().trim();
                const _plate = (_d.vehicle_plate || "").toString().trim().toUpperCase();
                const _cov = (q.coverage_request || "").toString().trim();
                const _isAutoGen = _cov.toLowerCase().startsWith("preventivo generato automaticamente dalla polizza");
                let quoteTitle;
                if (_VEHICLE_TYPES.has(q.type) && (_brand || _model)) {
                  quoteTitle = `Preventivo per ${[_brand, _model].filter(Boolean).join(" ").toUpperCase()}`;
                } else if (_isAutoGen || !_cov) {
                  // Fallback: preventivo generato dalla polizza (o senza descrizione) → usa il tipo
                  quoteTitle = `Preventivo per ${TYPE_LABEL_LIVE[q.type] || q.type || "assicurazione"}`;
                } else {
                  // Non veicolo → usa la descrizione del bene fornita dal cliente
                  quoteTitle = `Preventivo per ${_cov}`;
                }
                return (
                <div key={q.id} className={`relative rounded-lg border ${theme.border} ${theme.bg} overflow-hidden`} data-testid={`client-quote-${q.id}`}>
                  {/* Barra colorata laterale */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1 ${theme.bar}`} />
                  <div className="p-3 sm:p-4 pl-4 sm:pl-5">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${typeColor}`}>
                            {TYPE_LABEL_LIVE[q.type] || q.type}
                          </span>
                          {linkedOffer ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-[11px] sm:text-xs font-semibold text-emerald-700"
                              data-testid={`client-quote-offer-sent-${q.id}`}
                              title={`Offerta ${linkedOffer.offer_number || ""} inviata dall'agenzia`}
                            >
                              📧 Preventivo elaborato e inviato via email
                            </span>
                          ) : (
                            <StatusBadge status={q.status} />
                          )}
                          <span className="text-[10px] text-slate-400 ml-auto sm:ml-1">{formatDate(q.created_at)}</span>
                        </div>
                        {/* 🆕 Feb 2026 — Titolo dinamico: "Preventivo per MARCA MODELLO" (veicoli) o descrizione bene */}
                        <p className="text-sm font-semibold text-slate-800 mt-1.5 line-clamp-2" data-testid={`client-quote-title-${q.id}`}>
                          {quoteTitle}
                        </p>
                        {/* 🆕 Feb 2026 — Riga info: descrizione bene assicurato + targa (se presenti) */}
                        {(_plate || (_VEHICLE_TYPES.has(q.type) && (_brand || _model) && _cov && !_isAutoGen)) && (
                          <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`client-quote-info-${q.id}`}>
                            {_plate && (
                              <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-mono font-semibold text-slate-700">
                                🚗 {_plate}
                              </span>
                            )}
                            {_VEHICLE_TYPES.has(q.type) && (_brand || _model) && _cov && !_isAutoGen && (
                              <span className="text-[11px] text-slate-500 italic truncate">
                                {_cov}
                              </span>
                            )}
                          </div>
                        )}
                        {!_VEHICLE_TYPES.has(q.type) && _cov && !_isAutoGen && (
                          <p className="text-[11px] text-slate-500 italic mt-1 line-clamp-2">
                            {_cov}
                          </p>
                        )}
                        {q.details && Object.keys(q.details).length > 0 && (
                          <details className="mt-2" data-testid={`client-quote-details-${q.id}`}>
                            <summary className="text-[11px] text-blue-600 cursor-pointer font-medium hover:underline inline-flex items-center gap-1">📋 Dati specifici inviati</summary>
                            <div className="mt-2">
                              <QuoteDetailsRows details={q.details} />
                            </div>
                          </details>
                        )}
                        {/* 🆕 10/07/2026 — Premio stimato nascosto lato cliente: invita a contattare l'agente */}
                        {q.estimated_premium != null && (
                          <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-500 italic">
                            📞 Contatta l&apos;agente per il preventivo
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`open-thread-${q.id}`}
                        onClick={() => openThread(q)}
                        className="border-blue-200 text-blue-700 hover:bg-blue-50 h-8 text-xs px-2.5 shrink-0"
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        {expandedQuote === q.id ? "Chiudi" : "Chat"}
                      </Button>
                    </div>

                  {q.ai_summary && (
                    <div className="mt-4 rounded-md bg-blue-50/60 border border-blue-100 p-4">
                      <p className="text-xs uppercase tracking-wide text-blue-700 mb-2 inline-flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3" /> Risposta dell&apos;agenzia
                      </p>
                      <p className="text-sm text-slate-700 whitespace-pre-line">{q.ai_summary}</p>
                    </div>
                  )}

                  {expandedQuote === q.id && (
                    <div className="mt-4 border-t border-slate-100 pt-4 space-y-3" data-testid={`client-thread-${q.id}`}>
                      <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-thin">
                        {thread.length === 0 && (
                          <p className="text-xs text-slate-400">Nessun messaggio ancora. Scrivi qualcosa all&apos;agenzia.</p>
                        )}
                        {thread.map((m) => (
                          <div key={m.id} className={`flex ${m.author_role === "cliente" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.author_role === "cliente" ? "bg-blue-600 text-white" : "bg-slate-100"}`}>
                              <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">
                                {m.author_role === "cliente" ? "Tu" : m.author_name} · {formatDateTime(m.created_at)}
                              </div>
                              <div className="whitespace-pre-line">{m.body}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Textarea
                          data-testid={`client-reply-input-${q.id}`}
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          placeholder="Scrivi un messaggio alla tua agenzia…"
                          rows={2}
                          className="flex-1"
                          disabled={readOnly}
                        />
                        {!readOnly && (
                        <Button
                          data-testid={`client-send-reply-${q.id}`}
                          onClick={() => sendReply(q.id)}
                          disabled={!reply.trim()}
                          className="bg-[#0F172A] hover:bg-[#1E293B] self-end"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </section>

        <ClientPrivacyConsents />

        <footer className="mt-12 text-center text-xs text-slate-400">
          Accesso effettuato come <span className="font-medium">{user?.email}</span>
          <br />
          © {new Date().getFullYear()} {agency?.legal_name || agency?.name || "Assicura SRL"}
        </footer>
      </main>

      {/* Dialog cambio password */}
      <Dialog open={pwdOpen} onOpenChange={(o) => { setPwdOpen(o); if (!o) setPwdForm({ current: "", next: "", confirm: "" }); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-blue-600" /> Cambia password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password attuale <span className="text-red-500">*</span></Label>
              <Input
                data-testid="pwd-current"
                type="password"
                autoComplete="current-password"
                value={pwdForm.current}
                onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                placeholder="La tua password attuale"
              />
            </div>
            <div>
              <Label>Nuova password <span className="text-red-500">*</span></Label>
              <Input
                data-testid="pwd-next"
                type="password"
                autoComplete="new-password"
                value={pwdForm.next}
                onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
                placeholder="Almeno 8 caratteri"
              />
              <p className="text-[11px] text-slate-500 mt-1">Almeno 8 caratteri. Usa lettere, numeri e simboli per maggiore sicurezza.</p>
            </div>
            <div>
              <Label>Conferma nuova password <span className="text-red-500">*</span></Label>
              <Input
                data-testid="pwd-confirm"
                type="password"
                autoComplete="new-password"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                placeholder="Ripeti la nuova password"
                className={pwdForm.confirm && pwdForm.confirm !== pwdForm.next ? "border-rose-500" : ""}
              />
              {pwdForm.confirm && pwdForm.confirm !== pwdForm.next && (
                <p className="text-[11px] text-rose-600 mt-1">Le password non coincidono</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdBusy}>Annulla</Button>
            <Button
              data-testid="pwd-submit"
              onClick={submitChangePassword}
              disabled={pwdBusy || !pwdForm.current || !pwdForm.next || pwdForm.next !== pwdForm.confirm}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {pwdBusy ? "Salvataggio…" : "Aggiorna password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog Passkey / Biometria */}
      <Dialog open={passkeyDialogOpen} onOpenChange={setPasskeyDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="passkeys-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-blue-600" /> Accesso biometrico
            </DialogTitle>
          </DialogHeader>
          {!passkeySupported ? (
            <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-md p-3">
              Il tuo dispositivo o browser non supporta l&apos;accesso biometrico. Puoi continuare a usare il link di accesso o le credenziali email/password.
            </div>
          ) : (
            <div className="space-y-4 text-sm text-slate-700">
              <p>
                Attiva l&apos;accesso biometrico su questo dispositivo (impronta digitale, Face ID, Windows Hello) per
                entrare in modo più rapido e sicuro. Il metodo attuale rimane disponibile come alternativa.
              </p>
              <Button
                data-testid="register-passkey-btn"
                onClick={onRegisterPasskey}
                disabled={passkeyBusy}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Fingerprint className="h-4 w-4 mr-2" />
                {passkeyBusy ? "Attesa conferma…" : "Attiva su questo dispositivo"}
              </Button>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  I tuoi dispositivi ({passkeys.length})
                </div>
                {passkeysLoading ? (
                  <p className="text-xs text-slate-400 italic px-2">Caricamento…</p>
                ) : passkeys.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-2">Nessuna passkey ancora attivata.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {passkeys.map((pk) => (
                      <li key={pk.id} data-testid={`passkey-${pk.id}`} className="flex items-center justify-between gap-2 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{pk.name}</div>
                          <div className="text-[10px] text-slate-500 tabular-nums">
                            Aggiunta il {pk.created_at ? new Date(pk.created_at).toLocaleDateString("it-IT") : "—"}
                            {pk.last_used_at && ` · Ultimo uso ${new Date(pk.last_used_at).toLocaleDateString("it-IT")}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => onRenamePasskey(pk)}
                            className="text-slate-500 hover:text-slate-700 p-1"
                            title="Rinomina"
                            data-testid={`rename-passkey-${pk.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => onDeletePasskey(pk)}
                            className="text-rose-600 hover:bg-rose-50 rounded p-1"
                            title="Rimuovi"
                            data-testid={`delete-passkey-${pk.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p className="text-[11px] text-slate-500 italic">
                Nota: la passkey è legata a questo dispositivo e a questo dominio. Se cambi telefono o computer devi attivarla di nuovo lì.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasskeyDialogOpen(false)} data-testid="close-passkeys">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog: aggiorna scadenza documento (patente / revisione / bollo) */}
      <Dialog open={docUpdateOpen} onOpenChange={(o) => { if (!o) { setDocUpdateOpen(false); setDocUpdateTarget(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">📅 Aggiorna scadenza</DialogTitle>
          </DialogHeader>
          {docUpdateTarget && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600"><strong>{docUpdateTarget.label}</strong></p>
              <p className="text-xs text-slate-500">
                Scadenza attuale nel CRM: <strong>{docUpdateTarget.current ? formatDate(docUpdateTarget.current) : "— non comunicata —"}</strong>
              </p>
              <div>
                <Label className="text-xs">Nuova data di scadenza</Label>
                <DatePicker data-testid="doc-update-date-input"
                  value={docUpdateDate}
                  onChange={(e) => setDocUpdateDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Nota (opzionale)</Label>
                <Textarea
                  data-testid="doc-update-note"
                  rows={2}
                  value={docUpdateNote}
                  onChange={(e) => setDocUpdateNote(e.target.value)}
                  placeholder="Es. rinnovata oggi presso la motorizzazione."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocUpdateOpen(false)} disabled={docUpdateSubmitting}>Annulla</Button>
            <Button
              data-testid="doc-update-submit"
              disabled={docUpdateSubmitting || !docUpdateDate || !docUpdateTarget}
              onClick={async () => {
                setDocUpdateSubmitting(true);
                try {
                  const { data: res } = await api.post("/my/document-update", {
                    kind: docUpdateTarget.kind,
                    policy_id: docUpdateTarget.policy_id,
                    new_date: docUpdateDate,
                    note: docUpdateNote || null,
                  });
                  toast.success(res.updated ? "✓ Scadenza aggiornata. La tua agenzia è stata notificata." : "Data già coincidente. Nessuna modifica necessaria.");
                  setDocUpdateOpen(false);
                  // refresh portfolio
                  const { data: pd } = await api.get("/my/portfolio");
                  setData(pd);
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setDocUpdateSubmitting(false); }
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {docUpdateSubmitting ? "Invio…" : "Invia all'agenzia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Bottom-nav mobile — visibile solo su smartphone (< md) */}
      {(() => {
        const inProgressCount = myQuotes.filter((q) => q.status === "nuovo" || q.status === "in_lavorazione").length;
        // Conta scadenze documenti entro 30 giorni (cliente + veicoli)
        const daysUntil = (iso) => {
          if (!iso) return null;
          const t = new Date(); t.setHours(0,0,0,0);
          const e = new Date(iso); e.setHours(0,0,0,0);
          return Math.ceil((e - t) / 86400000);
        };
        // Deduplica scadenze veicoli per targa
        const vehiclePlates2 = new Map();
        (data.policies || []).filter((p) => p.type === "auto" && p.vehicle_plate).forEach((p) => {
          const plate = String(p.vehicle_plate).trim().toUpperCase();
          if (!plate) return;
          const entry = vehiclePlates2.get(plate) || { next_review_date: null, bollo_due_date: null };
          if (p.next_review_date && (!entry.next_review_date || new Date(p.next_review_date) > new Date(entry.next_review_date))) entry.next_review_date = p.next_review_date;
          if (p.bollo_due_date && (!entry.bollo_due_date || new Date(p.bollo_due_date) > new Date(entry.bollo_due_date))) entry.bollo_due_date = p.bollo_due_date;
          vehiclePlates2.set(plate, entry);
        });
        const docDates = [
          data.client?.driving_license_expires_at,
          data.client?.id_card_expires_at,
          data.client?.health_card_expires_at,
          ...[...vehiclePlates2.values()].flatMap((v) => [v.next_review_date, v.bollo_due_date]),
        ];
        const deadlinesCount = docDates.filter((d) => {
          const days = daysUntil(d);
          return days !== null && days <= 30; // include scadute (giorni negativi)
        }).length;

        const items = [
          { id: "top", label: "Home", icon: Home, target: "client-top", badge: 0 },
          { id: "policies", label: "Polizze", icon: ShieldCheck, target: "client-policies-section", badge: 0 },
          { id: "quotes", label: "Preventivi", icon: FileText, target: "client-quotes-section", badge: inProgressCount },
          { id: "deadlines", label: "Scadenze", icon: AlertTriangle, target: "client-document-deadlines", badge: deadlinesCount },
        ];
        return (
          <nav
            data-testid="client-bottom-nav"
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A192F]/95 backdrop-blur border-t border-white/10 text-white shadow-2xl"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            aria-label="Navigazione area cliente"
          >
            <div className="max-w-3xl mx-auto grid grid-cols-4">
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.id}
                    type="button"
                    data-testid={`bottom-nav-${it.id}`}
                    onClick={() => {
                      const el = document.getElementById(it.target);
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      } else if (it.target === "client-top") {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    className="relative flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[10px] font-medium text-slate-300 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors"
                    aria-label={`${it.label}${it.badge > 0 ? ` (${it.badge} da controllare)` : ""}`}
                  >
                    <div className="relative">
                      <Icon className="h-5 w-5" />
                      {it.badge > 0 && (
                        <span
                          data-testid={`bottom-nav-badge-${it.id}`}
                          className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-[#0A192F] shadow-md tabular-nums"
                          aria-hidden="true"
                        >
                          {it.badge > 9 ? "9+" : it.badge}
                        </span>
                      )}
                    </div>
                    <span className="leading-none">{it.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        );
      })()}
    </div>
  );
}
